import React, { useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { XMarkIcon, ChatBubbleLeftEllipsisIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../context/AuthContext'
import { zglosBlad } from '../api/account'

/**
 * Formularz „Zgłoś błąd” (decyzja właściciela 2026-10-13): wiadomość do 1000 znaków idzie mailem
 * przez backend (`POST /api/v1/account/zgloszenie-bledu`, `accounts/zgloszenie_bledu.py`) na kontakt@voltvalue.pl.
 *
 * Jeden komponent w dwóch miejscach: w dymku w lewym dolnym rogu (`ZglosBlad`) i w oknie otwieranym
 * z menu konta (`OknoZgloszenia`, ostatnia pozycja w `UserMenu`).
 *
 * Wiadomość nie może zginąć: przy błędzie tekst zostaje w polu, a obok pokazujemy adres do skopiowania
 * i link do poczty z gotową treścią. Szkic żyje w pamięci modułu, więc przeżywa zwinięcie dymka
 * i przejście między dymkiem a oknem z menu (nie zapisujemy go w przeglądarce).
 */

export const ADRES_ZGLOSZEN = 'kontakt@voltvalue.pl'
export const MAX_ZNAKOW = 1000

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const szkic = { tekst: '', email: null }
// Dla testów: czysty stan między przypadkami.
export const wyczyscSzkic = () => { szkic.tekst = ''; szkic.email = null }

// Kody z backendu (`KOMUNIKATY` w `zgloszenie_bledu.py`) na klucze tłumaczeń. Błędy, przy których
// użytkownik nic nie poprawi, pokazują adres zapasowy.
const KOMUNIKAT = {
  pusta: 'zglos.blad_pusta',
  za_dluga: 'zglos.blad_za_dluga',
  same_linki: 'zglos.blad_same_linki',
  zly_email: 'zglos.blad_email',
  limit: 'zglos.blad_limit',
  wysylka: 'zglos.blad_wysylka',
}
const Z_ADRESEM = new Set(['limit', 'wysylka', 'siec'])

const FormularzZgloszenia = ({ onZamknij }) => {
  const { t } = useTranslation('wspolne')
  const { user } = useAuth()
  const poleId = useId()
  const emailId = useId()
  const poleRef = useRef(null)
  const [tekst, setTekst] = useState(szkic.tekst)
  // Zalogowanemu podpowiadamy adres konta; pole można wyczyścić i wtedy zostaje puste.
  const [email, setEmail] = useState(szkic.email ?? user?.email ?? '')
  const [strona, setStrona] = useState('')
  const [stan, setStan] = useState('edycja')
  const [blad, setBlad] = useState(null)
  const [skopiowano, setSkopiowano] = useState(false)

  useEffect(() => { poleRef.current?.focus() }, [])

  // Profil potrafi dojść po otwarciu formularza: wtedy wpisujemy adres, o ile nikt nie ruszał pola.
  const adresKonta = user?.email
  useEffect(() => {
    if (adresKonta && szkic.email === null) setEmail((e) => e || adresKonta)
  }, [adresKonta])

  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape') onZamknij?.() }
    document.addEventListener('keydown', esc)
    return () => document.removeEventListener('keydown', esc)
  }, [onZamknij])

  const zmienTekst = (v) => { szkic.tekst = v; setTekst(v); if (blad) setBlad(null) }
  const zmienEmail = (v) => { szkic.email = v; setEmail(v); if (blad === 'zly_email') setBlad(null) }

  const wyslij = async (e) => {
    e.preventDefault()
    const wiadomosc = tekst.trim()
    if (!wiadomosc) { setBlad('pusta'); poleRef.current?.focus(); return }
    if (email.trim() && !EMAIL.test(email.trim())) { setBlad('zly_email'); return }
    setBlad(null)
    setStan('wysylanie')
    const res = await zglosBlad({
      wiadomosc,
      email: email.trim(),
      sciezka: window.location.pathname,
      szerokosc: window.innerWidth || null,
      strona_firmy: strona,
    })
    if (res.ok) {
      wyczyscSzkic()
      setTekst('')
      setStan('wyslane')
      return
    }
    setStan('edycja')
    setBlad(res.httpStatus ? (res.data?.kod || 'wysylka') : 'siec')
  }

  const kopiuj = async () => {
    try {
      await navigator.clipboard.writeText(ADRES_ZGLOSZEN)
      setSkopiowano(true)
    } catch {
      // Bez dostępu do schowka adres i tak jest widoczny i da się go zaznaczyć.
    }
  }

  if (stan === 'wyslane') {
    return (
      <div className="mt-3">
        <p role="status" className="text-sm font-semibold text-emerald-700">{t('zglos.dziekujemy')}</p>
        <button
          type="button"
          onClick={onZamknij}
          className="mt-3 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700
                     hover:bg-gray-50 transition-colors"
        >
          {t('zglos.zamknij')}
        </button>
      </div>
    )
  }

  const temat = t('zglos.temat')
  const mailto = `mailto:${ADRES_ZGLOSZEN}?subject=${encodeURIComponent(temat)}&body=${encodeURIComponent(tekst.trim())}`

  return (
    <form onSubmit={wyslij} noValidate className="mt-3">
      <label htmlFor={poleId} className="sr-only">{t('zglos.pole_wiadomosc')}</label>
      <textarea
        id={poleId}
        ref={poleRef}
        value={tekst}
        onChange={(e) => zmienTekst(e.target.value)}
        maxLength={MAX_ZNAKOW}
        rows={5}
        placeholder={t('zglos.placeholder')}
        className="block w-full resize-none rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm
                   text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none
                   focus:ring-2 focus:ring-blue-500/30"
      />
      <p className="mt-1 text-right text-xs tabular-nums text-gray-400" aria-live="polite">
        {tekst.length}/{MAX_ZNAKOW}
      </p>

      <label htmlFor={emailId} className="block text-xs text-gray-500">{t('zglos.pole_email')}</label>
      <input
        id={emailId}
        type="email"
        value={email}
        onChange={(e) => zmienEmail(e.target.value)}
        autoComplete="email"
        className="mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900
                   focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
      />

      {/* Pułapka na boty: człowiek tego pola nie widzi ani nie dojdzie do niego klawiszem Tab. */}
      <div aria-hidden="true" className="absolute -left-[9999px] h-px w-px overflow-hidden">
        <label>
          Strona firmy
          <input type="text" name="strona_firmy" tabIndex={-1} autoComplete="off"
                 value={strona} onChange={(e) => setStrona(e.target.value)} />
        </label>
      </div>

      {blad && (
        <div role="alert" className="mt-2 text-xs leading-5 text-red-700">
          <p>{t(KOMUNIKAT[blad] || 'zglos.blad_wysylka')}</p>
          {Z_ADRESEM.has(blad) && (
            <p className="mt-1 text-gray-600">
              {t('zglos.zapasowy')}{' '}
              <a href={mailto} className="font-semibold text-blue-700 underline">{ADRES_ZGLOSZEN}</a>{' '}
              <button type="button" onClick={kopiuj} className="font-medium text-blue-700 hover:underline">
                {skopiowano ? t('zglos.skopiowano') : t('zglos.kopiuj')}
              </button>
            </p>
          )}
        </div>
      )}

      <button
        type="submit"
        disabled={stan === 'wysylanie'}
        className="mt-3 inline-flex items-center justify-center w-full px-3 py-2 rounded-lg
                   bg-blue-600 hover:bg-blue-500 active:bg-blue-700 disabled:opacity-60 text-white
                   text-sm font-semibold transition-colors"
      >
        {stan === 'wysylanie' ? t('zglos.wysylanie') : t('zglos.wyslij')}
      </button>
    </form>
  )
}

/** Ten sam formularz w oknie na środku ekranu (pozycja „Zgłoś błąd” w menu konta). Portal do `body`,
 *  bo pasek nawigacji tworzy własny kontekst warstw i `fixed` w nim nie przykryłby strony. */
export const OknoZgloszenia = ({ onZamknij }) => {
  const { t } = useTranslation('wspolne')
  const tytulId = useId()
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-gray-900/40 p-4 sm:items-center print:hidden"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onZamknij() }}
    >
      <div role="dialog" aria-modal="true" aria-labelledby={tytulId}
           className="relative w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
        <button
          type="button"
          onClick={onZamknij}
          aria-label={t('zglos.zamknij')}
          className="absolute top-3 right-3 p-1 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100
                     transition-colors"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
        <h2 id={tytulId} className="flex items-center gap-2 pr-8 text-lg font-bold text-gray-900">
          <ChatBubbleLeftEllipsisIcon className="w-5 h-5 text-blue-600 flex-shrink-0" />
          {t('zglos.tytul')}
        </h2>
        <p className="mt-1 text-sm text-gray-500">{t('zglos.opis')}</p>
        <FormularzZgloszenia onZamknij={onZamknij} />
      </div>
    </div>,
    document.body,
  )
}

export default FormularzZgloszenia
